import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import type { TopIssue } from '@vehix/shared/types/recallSnapshots';

interface Props {
  issues: TopIssue[];
  onSelect?: (issue: TopIssue) => void;
}

function relativeAge(at: string | null): string {
  if (!at) return '';
  const ms = Date.now() - new Date(at).getTime();
  const hours = Math.round(ms / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export const TopIssuesList: React.FC<Props> = ({ issues, onSelect }) => {
  return (
    <Card className='p-4'>
      <div className='flex items-baseline justify-between gap-2 mb-3'>
        <h3 className='text-sm font-semibold'>Top issues</h3>
        <span className='text-[11px] text-muted-foreground'>
          {issues.length === 0 ? 'No issues' : `${issues.length} total`}
        </span>
      </div>

      {issues.length === 0 ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground py-3'>
          <CheckCircle2 className='h-4 w-4 text-emerald-500' />
          All assets healthy
        </div>
      ) : (
        <ul className='space-y-2'>
          {issues.slice(0, 8).map(issue => {
            const Icon = issue.severity === 'critical' ? AlertOctagon : AlertTriangle;
            const sevColor =
              issue.severity === 'critical'
                ? 'text-red-600 dark:text-red-400'
                : 'text-amber-600 dark:text-amber-400';
            return (
              <li key={issue.id}>
                <button
                  type='button'
                  onClick={onSelect ? () => onSelect(issue) : undefined}
                  className={cn(
                    'w-full text-left flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors',
                    !onSelect && 'cursor-default hover:bg-transparent'
                  )}
                >
                  <Icon className={cn('h-4 w-4 flex-shrink-0 mt-0.5', sevColor)} />
                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-medium truncate'>
                      {issue.tenant_name} · {issue.oem}
                    </p>
                    <p className='text-xs text-muted-foreground truncate'>
                      {issue.reason}
                      {issue.occurred_at && ` · ${relativeAge(issue.occurred_at)}`}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default TopIssuesList;
